/**
 * Checks if a field is disabled based on its decorators.
 *
 * This function searches the field's decorators for a "edit".
 * If found, it returns true if the "edit" property is true, otherwise it returns false.
 *
 * @param {Object} field - The field object to check.
 * @returns {boolean} - Returns true if the field is disabled, otherwise false.
 */
export const isFieldDisabled = (field) => {
   if (!field) return false;

   if (
      field?.decorators !== "" &&
      field?.decorators !== null &&
      field?.decorators?.find((el) => el.name === "EditableDecorator")
   ) {
      const decorator = field?.decorators?.find((el) => el.name === "EditableDecorator");
      return !decorator?.properties?.edit;
   }

   return false;
};

/**
 * Retrieves the position of a field based on its decorators.
 *
 * This function searches the field's decorators for a "PositionDecorator".
 * If found, it returns the value of the "position" property, otherwise it returns "left".
 *
 * @param {Object} field - The field object to check.
 * @returns {string|undefined} - Returns the position if found, otherwise undefined.
 */
export const getFieldPosition = (field) => {
   if (!field) return false;
   if (field?.type === "List") return "center";
   if (!field?.decorators) return "left";
   if (
      field?.decorators?.hasOwnProperty("position") &&
      field?.decorators !== "" &&
      field?.decorators !== null
   ) {
      return field?.decorators?.position;
   } else return "left";
};

/**
 * Determines if a field should be hidden based on its decorators. We are using it in formV3Component file.
 *
 * @param {Object} field - The field object to check.
 * @param {Object} field.decorators - The decorators object within the field.
 * @param {boolean} [field.decorators.hide] - A boolean flag indicating if the field should be hidden.
 * @returns {boolean} - Returns `true` if the field should be hidden, otherwise `false`.
 *
 * @example
 * const field = {
 *   decorators: {
 *     hide: true
 *   }
 * };
 * console.log(isFieldHidden(field)); // Output: true
 */
export const isFieldHidden = (field) => {
   if (!field) return false;
   if (
      field?.decorators !== "" &&
      field?.decorators !== null &&
      field?.decorators?.find((el) => el.name === "HideFieldDecorator")
   ) {
      const decorator = field?.decorators?.find((el) => el.name === "HideFieldDecorator");
      return decorator?.properties?.hide;
   } else return false;
};

/**
 * Returns the value format string from ValueFormatDecorator, or null if not set.
 */
export const getValueFormat = (field): string | null => {
   if (!field?.decorators) return null;
   const decorator = field.decorators.find((el) => el?.name === "ValueFormatDecorator");
   return decorator?.properties?.format || null;
};

export const toDisplayString = (value): string => {
   if (value === null || value === undefined) return "";
   if (typeof value === "object") {
      return String(value.decoratedValue ?? value.originalValue ?? "");
   }
   return String(value);
};

export const isFieldQR = (field) => {
   if (!field) return false;
   if (
      field?.decorators !== "" &&
      field?.decorators !== null &&
      field?.decorators?.find((el) => el.name === "QrCodeDecorator")
   ) {
      const decorator = field?.decorators?.find((el) => el.name === "QrCodeDecorator");
      return decorator ? true : false;
   } else return false;
};
